<template>
  <AccountCard :title="$t('pages.account.settings.sections.password.title')">
    <div class="flex flex-row flex-wrap items-center gap-4">
      <VForm
        :validation-schema="Schema"
        :initial-values="Schema.getDefault()"
        v-slot="{ handleSubmit }"
        as="div"
        class="flex-1 min-w-[15rem]"
      >
        <form @submit="handleSubmit($event, mutate)" class="max-w-lg">
          <CommonAlert v-if="error" type="error" :content="error.message" class="mb-6 mt-4" />
          <div class="w-full mt-2">
            <div class="flex flex-row flex-wrap gap-x-2 md:pt-4">
              <BaseVTextInput
                type="password"
                :label="$t('form.current_password')"
                name="password"
                class="flex-1 basis-full"
              />
              <span class="flex flex-row items-center gap-2 mt-2 text-sm">
                <CInfoE class="text-[1em]" />
                {{ $t("pages.account.signup.texts.password_requirements") }}
              </span>
              <BaseVTextInput
                type="password"
                :label="$t('form.new_password')"
                name="new_password"
                class="flex-1 basis-full md:mt-6"
              />
              <BaseVTextInput
                type="password"
                :label="$t('form.new_password_confirmation')"
                name="new_password_confirmation"
                class="flex-1 basis-full md:mt-6"
              />
            </div>

            <div class="flex flex-row flex-wrap gap-4 items-center mt-8">
              <BaseButton
                type="submit"
                class="w-auto max-w-full whitespace-nowrap text-[1rem]"
                :loading="pending"
                size="medium"
              >
                {{ $t("pages.account.buttons.change_password") }}
              </BaseButton>

              <div class="flex-1 basis-1/3 text-primary shrink-0">
                <div class="flex flex-row gap-2 items-center" v-if="success">
                  <CheckCircleIcon class="w-[1.5em] h-[1.5em] shrink-0" />
                  <span>{{ $t("pages.account.texts.changes_saved") }}</span>
                </div>
              </div>
            </div>
          </div>
        </form>
      </VForm>
    </div>
  </AccountCard>
</template>

<script setup lang="ts">
import { CheckCircleIcon } from "@heroicons/vue/solid";
import { AxiosError } from "axios";
import * as yup from "yup";
import CInfoE from "~/components/icons/CIconE.vue";
import type { User } from "~/entities/User";
import AccountCard from "../components/account-card.vue";

interface AccountSettingsPersonalDetailsProps {
  profileDetails: User.ProfileDetails;
}
const props = defineProps<AccountSettingsPersonalDetailsProps>();

const Schema = yup.object({
  type: yup.string().optional().default("password"),
  password: yup.string().required(),
  new_password: yup.string().required(),
  new_password_confirmation: yup
    .string()
    .oneOf([yup.ref("new_password")])
    .required(),
});
type Schema = yup.InferType<typeof Schema>;

const error = ref<Error>();
const { mutate, pending, success } = useMutation({
  mutationFn: async (credentials: Schema) =>
    useAxios()
      .put("/profile/update", credentials)
      .then((res) => res.data),
  onError(err) {
    error.value = err;
    if (err instanceof AxiosError) error.value = new Error(err.response?.data.message);
  },
  onSuccess() {
    error.value = undefined;
  },
});
</script>
